import { Org } from "@/types/org";
import { orgFetchs } from "@/apis/org";
import { useEffect, useRef, useState } from "react";
import { searchTreeList } from "../utils/searchTree";

type IFetchsFunc = () => void;
type IAddFunc = (info: Org) => void;
type IUpdateFunc = (info: Org) => void;
type IDeleteFunc = (info: Org) => void;
type IFetchsByNameFunc = (value: string, isFetch: boolean) => void

type IUseOrgList = (props: {
    keyword?: string;
}) => [
        Array<Org>,
        number,
        boolean,
        { add: IAddFunc; update: IUpdateFunc; deleteItem: IDeleteFunc, fetchsByName: IFetchsByNameFunc, fetchs: IFetchsFunc }
    ];


const useOrgList: IUseOrgList = (props) => {
    const { keyword } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [list, setList] = useState<Array<Org>>([]);
    const [total, setTotal] = useState<number>(0);
    const [backupList, setBackupList] = useState<Array<Org>>([]);
    const isUpdateBackupList = useRef<boolean>(true)

    useEffect(() => {
      fetchs()
    }, [keyword]);

    useEffect(() => {
      if(isUpdateBackupList.current) setBackupList(list)
    }, [list])

    const fetchs:IFetchsFunc = () => {
      setLoading(true);
      orgFetchs({ body: keyword }).then((res) => {
          setList(res.body)
      }).finally(() => setLoading(false));
    }

    const add: IAddFunc = (item) => {
      isUpdateBackupList.current = true
      setLoading(true)
      if(item.pId){
        setList(updateTreeData(list, item.pId, 'add', item))
      } else {
        setList([item, ...list])
      }
      setLoading(false)
    };

    const update: IUpdateFunc = (upItem) => {
      isUpdateBackupList.current = true
      setLoading(true)
      if(upItem.pId) {
        setList(updateTreeData(list, upItem.pId, 'update', upItem))
      } else {
        setList((prev) => {
            return prev?.map((item) => (item.id === upItem.id ? upItem : item));
        });
      }
      setLoading(false)
    };

    const deleteItem: IDeleteFunc = (delItem) => {
      isUpdateBackupList.current = true
      if(delItem.pId) {
        setList(updateTreeData(list, delItem.pId, 'delete', delItem))
      } else {
        setList(list.filter(item => item.id !== delItem.id));
      }
    };

    const fetchsByName:IFetchsByNameFunc = (value: string, isFetch: boolean) => {
      setLoading(true)
      const nameVal = value.trim()
      if(nameVal && nameVal.length > 0){
        isUpdateBackupList.current = false
        if(isFetch) {
          orgFetchs().then((res) => {
            setList(res.body)
            setList(searchTreeList(res.body, nameVal))
          })
        } else {
          setList(searchTreeList(backupList, nameVal))
        }
      } else {
        isUpdateBackupList.current = true
        fetchs()
      }
      setLoading(false)
    }

    const updateTreeData = (list: Org[], pId: string | null, action: 'add' | 'delete' | 'update', item: Org): Org[] => {  
        const updatedList = JSON.parse(JSON.stringify(list));
        const findAndUpdateParent = (parentId: string | null, currentList: Org[]) => {  
            for (let i = 0; i < currentList.length; i++) {  
                const node = currentList[i];  
                if (node.id === parentId) {  
                    if (action === 'add') {  
                        (node.subs || (node.subs = [])).push(item);  
                    } else if (action === 'delete') {
                      if(node.subs) {
                        node.subs = node.subs?.filter((sub:Org) => sub.id !== item.id);
                      }
                    } else if (action === 'update') {
                        node.subs = node.subs?.map((sub:Org) => sub.id === item.id ? {...sub, ...item} : sub);  
                    }  
                    return;  
                }  
                if (node.subs) {  
                    findAndUpdateParent(parentId, node.subs);  
                }  
            }  
        }
        findAndUpdateParent(pId, updatedList);  
        return updatedList;  
    }

    return [list, total, loading, { add, update, deleteItem, fetchsByName, fetchs }];
};

export default useOrgList;
